import React, { Fragment, useEffect, useRef, useState } from "react";
import { Col, FormGroup, Label, Row } from "reactstrap";
import AsyncSelect from "react-select/async";
import {
    categoryAutoSearchApi,
    getCategoryMeasurementTypesApi,
    getMeasurementTypesApi,
    getMeasurementUnitsApi,
    sizeAutoSearchApi,
    sellerAutoSearchApi,
    brandAutoSearchApi,
    getDefaultSellerApi,
} from "../../../../services/manage-product/setting/sizeCategoryMeasurementService";

const toTypeOption = (t) => ({ value: t.name, label: t.display_name });

const SizeCategoryMeasurementForm = (props) => {
    const { errors, register, setValue, trigger, getValues, isSuperAdmin = false, onLoadingChange } = props;

    const [allTypeOptions, setAllTypeOptions]         = useState([]);
    const [activeTypeOptions, setActiveTypeOptions]   = useState([]);
    const [unitOptions, setUnitOptions]               = useState([]);
    const [selectedSeller, setSelectedSeller]         = useState(null);
    const [selectedBrand, setSelectedBrand]           = useState(null);
    const [formLoading, setFormLoading]               = useState(true);
    const measurementSelectRef                        = useRef(null);
    const brandSelectRef                              = useRef(null);

    useEffect(() => {
        const existingSeller = getValues('seller_option');
        const existingBrand  = getValues('brand_option');
        const isEditMode     = !!(getValues('id'));

        if (existingSeller) setSelectedSeller(existingSeller);
        if (existingBrand)  setSelectedBrand(existingBrand);

        const typesPromise = getMeasurementTypesApi().then(({ response }) => {
            if (Array.isArray(response)) {
                const opts = response.map(toTypeOption);
                setAllTypeOptions(opts);

                const existingCategoryId = getValues('category_id');
                if (existingCategoryId) {
                    return getCategoryMeasurementTypesApi({ category_id: existingCategoryId, base: 'default' }).then(({ response: filtered }) => {
                        if (Array.isArray(filtered) && filtered.length > 0) {
                            setActiveTypeOptions(filtered.map(toTypeOption));
                        } else {
                            setActiveTypeOptions(opts);
                        }
                    });
                } else {
                    setActiveTypeOptions(opts);
                }
            }
        });

        const unitsPromise = getMeasurementUnitsApi().then(({ response }) => {
            if (Array.isArray(response)) setUnitOptions(response);
        });

        const sellerPromise = isEditMode
            ? Promise.resolve()
            : getDefaultSellerApi().then(({ response }) => {
                if (response) {
                    const option = { value: response.id, label: response.name };
                    setSelectedSeller(option);
                    setValue('seller_id', response.id);

                    if (!isSuperAdmin) {
                        return brandAutoSearchApi({ seller_id: response.id, value: '' }).then(({ response: brands }) => {
                            if (Array.isArray(brands) && brands.length === 1) {
                                const b = brands[0];
                                const brandOption = { value: b.id, label: b.name };
                                setSelectedBrand(brandOption);
                                setValue('brand_id', b.id);
                                setValue('brand_option', brandOption);
                            }
                        });
                    }
                }
            });

        Promise.all([typesPromise, unitsPromise, sellerPromise]).then(() => {
            setFormLoading(false);
            if (onLoadingChange) onLoadingChange(false);
        });
    }, []);

    const handleCategoryChange = (option) => {
        setValue('category_id', option?.value ?? null);
        trigger('category_id');
        setValue('measurement_type', null);
        if (measurementSelectRef.current) measurementSelectRef.current.clearValue();

        if (option?.value) {
            getCategoryMeasurementTypesApi({ category_id: option.value, base: 'default' }).then(({ response }) => {
                if (Array.isArray(response) && response.length > 0) {
                    setActiveTypeOptions(response.map(toTypeOption));
                } else {
                    setActiveTypeOptions(allTypeOptions);
                }
            });
        } else {
            setActiveTypeOptions(allTypeOptions);
        }
    };

    const handleSellerChange = (option) => {
        setSelectedSeller(option ?? null);
        setValue('seller_id', option?.value ?? null);
        setValue('seller_option', option ?? null);
        trigger('seller_id');
        // Clear brand when seller changes
        setSelectedBrand(null);
        setValue('brand_id', null);
        setValue('brand_option', null);
        if (brandSelectRef.current) brandSelectRef.current.clearValue();
    };

    const handleBrandChange = (option) => {
        setSelectedBrand(option ?? null);
        setValue('brand_id', option?.value ?? null);
        setValue('brand_option', option ?? null);
        trigger('brand_id');
    };

    const categoryPromiseOptions = (inputValue) =>
        categoryAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((c) => ({ value: c.id, label: c.name })) : []
        );

    const sizePromiseOptions = (inputValue) =>
        sizeAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((s) => ({ value: s.id, label: s.name })) : []
        );

    const measurementTypePromiseOptions = (inputValue) =>
        Promise.resolve(
            activeTypeOptions.filter((o) =>
                o.label.toLowerCase().includes(inputValue.toLowerCase())
            )
        );

    const sellerPromiseOptions = (inputValue) =>
        sellerAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((s) => ({ value: s.id, label: s.name })) : []
        );

    const brandPromiseOptions = (inputValue) =>
        brandAutoSearchApi({ value: inputValue, seller_id: selectedSeller?.value ?? null }).then(({ response }) =>
            Array.isArray(response) ? response.map((b) => ({ value: b.id, label: b.name })) : []
        );

    if (formLoading) {
        return (
            <div className="text-center py-4">
                <i className="fa fa-spinner fa-spin fa-2x text-primary" />
            </div>
        );
    }

    return (
        <Fragment>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Category <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <AsyncSelect
                            isClearable
                            cacheOptions
                            defaultOptions
                            defaultValue={getValues('category')}
                            loadOptions={categoryPromiseOptions}
                            onChange={handleCategoryChange}
                            className={errors.category_id ? 'is-invalid' : ''}
                            placeholder="Search category..."
                        />
                        <div className="invalid-feedback">{errors.category_id?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Size <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <AsyncSelect
                            isClearable
                            cacheOptions
                            defaultOptions
                            defaultValue={getValues('size')}
                            loadOptions={sizePromiseOptions}
                            onChange={(e) => { setValue('size_matrix_id', e?.value ?? null); trigger('size_matrix_id'); }}
                            className={errors.size_matrix_id ? 'is-invalid' : ''}
                            placeholder="Search size..."
                        />
                        <div className="invalid-feedback">{errors.size_matrix_id?.message}</div>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Measurement Type <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <AsyncSelect
                            ref={measurementSelectRef}
                            isClearable
                            cacheOptions={false}
                            defaultOptions={activeTypeOptions}
                            defaultValue={getValues('measurement_type_option')}
                            loadOptions={measurementTypePromiseOptions}
                            onChange={(e) => { setValue('measurement_type', e?.value ?? null); trigger('measurement_type'); }}
                            className={errors.measurement_type ? 'is-invalid' : ''}
                            placeholder="Select measurement type..."
                        />
                        <div className="invalid-feedback">{errors.measurement_type?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Unit <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <AsyncSelect
                            isClearable
                            cacheOptions
                            defaultOptions={unitOptions}
                            defaultValue={getValues('measurement_unit_option')}
                            loadOptions={(v) => Promise.resolve(unitOptions.filter((o) => o.label.toLowerCase().includes(v.toLowerCase())))}
                            onChange={(e) => { setValue('measurement_unit_type', e?.value ?? null); trigger('measurement_unit_type'); }}
                            className={errors.measurement_unit_type ? 'is-invalid' : ''}
                            placeholder="cm / inch"
                        />
                        <div className="invalid-feedback">{errors.measurement_unit_type?.message}</div>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Min Value <span style={{ color: 'red' }}>*</span>
                        </Label>
                        <input
                            type="number"
                            step="0.01"
                            {...register('measurement_min_value', { onChange: () => trigger('measurement_max_value') })}
                            className={`form-control ${errors.measurement_min_value ? 'is-invalid' : ''}`}
                            placeholder="e.g. 86.4"
                        />
                        <div className="invalid-feedback">{errors.measurement_min_value?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label className="col-form-label">Max Value</Label>
                        <input
                            type="number"
                            step="0.01"
                            {...register('measurement_max_value')}
                            className={`form-control ${errors.measurement_max_value ? 'is-invalid' : ''}`}
                            placeholder="e.g. 91.4 (leave blank for single value)"
                        />
                        <div className="invalid-feedback">{errors.measurement_max_value?.message}</div>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label className="col-form-label">UK Size</Label>
                        <input
                            type="text"
                            {...register('uk_size')}
                            className={`form-control ${errors.uk_size ? 'is-invalid' : ''}`}
                            placeholder="e.g. 6"
                        />
                        <div className="invalid-feedback">{errors.uk_size?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label className="col-form-label">EU Size</Label>
                        <input
                            type="text"
                            {...register('eu_size')}
                            className={`form-control ${errors.eu_size ? 'is-invalid' : ''}`}
                            placeholder="e.g. 39"
                        />
                        <div className="invalid-feedback">{errors.eu_size?.message}</div>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label className="col-form-label">US Size</Label>
                        <input
                            type="text"
                            {...register('us_size')}
                            className={`form-control ${errors.us_size ? 'is-invalid' : ''}`}
                            placeholder="e.g. 8.5"
                        />
                        <div className="invalid-feedback">{errors.us_size?.message}</div>
                    </FormGroup>
                </Col>
            </Row>

            {isSuperAdmin && (
                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <Label className="col-form-label">
                                Seller <span style={{ color: 'red' }}>*</span>
                            </Label>
                            <AsyncSelect
                                isClearable
                                cacheOptions
                                defaultOptions
                                value={selectedSeller}
                                loadOptions={sellerPromiseOptions}
                                onChange={handleSellerChange}
                                placeholder="Search seller..."
                                className={errors.seller_id ? 'is-invalid' : ''}
                            />
                            <div className="invalid-feedback">{errors.seller_id?.message}</div>
                        </FormGroup>
                    </Col>
                </Row>
            )}
            <Row>
                <Col md={12}>
                    <FormGroup>
                        <Label className="col-form-label">
                            Brand <small className="text-muted">(blank = all brands)</small>
                        </Label>
                        <AsyncSelect
                            ref={brandSelectRef}
                            key={selectedSeller?.value ?? 'no-seller'}
                            isClearable
                            cacheOptions
                            defaultOptions={isSuperAdmin ? !!selectedSeller : true}
                            value={selectedBrand}
                            loadOptions={brandPromiseOptions}
                            onChange={handleBrandChange}
                            placeholder="Search brand..."
                            isDisabled={isSuperAdmin && !selectedSeller}
                        />
                    </FormGroup>
                </Col>
            </Row>
        </Fragment>
    );
};

export default SizeCategoryMeasurementForm;
